import { useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp } from '../../services/authService';
import useAuthStore from '../../store/authStore';
import { ChevronLeft, User, Scale, AlertCircle, FileText } from 'lucide-react';

// Input component for form fields
const Input = ({
  label,
  id,
  name,
  type = 'text',
  value,
  onChange,
  required = false,
  placeholder = '',
  className = '',
  ...props
}: {
  label: string;
  id: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  [key: string]: any;
}) => (
  <div className={`mb-4 ${className}`}>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
      {...props}
    />
  </div>
);

type UserRole = 'client' | 'lawyer';

interface BaseUserData {
  fullName: string;
  phone: string;
  email: string;
  password: string;
  role: UserRole;
  nationalId?: string;
  iban?: string;
  jobTitle?: string;
  monthlyIncome?: string | number;
  monthlyCommitments?: string | number;
  licenseNumber?: string;
  commercialRegistryNumber?: string;
  officeLocation?: string;
  licensePhoto?: File | null;
  registryPhoto?: File | null;
}

interface ClientUserData extends BaseUserData {
  role: 'client';
  nationalId: string;
  iban: string;
  jobTitle: string;
  monthlyIncome: string;
  monthlyCommitments: string;
}

interface LawyerUserData extends BaseUserData {
  role: 'lawyer';
  licenseNumber: string;
  commercialRegistryNumber: string;
  officeLocation: string;
  licensePhoto: File | null;
  registryPhoto: File | null;
}

type UserData = ClientUserData | LawyerUserData;

const SignupPage = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [role, setRole] = useState<UserRole>('client');
  const [error, setError] = useState('');
  // Define a more specific type for our form data that makes required fields non-optional
  type SignupFormData = Omit<BaseUserData, 'monthlyIncome' | 'monthlyCommitments'> & {
    monthlyIncome: number;
    monthlyCommitments: number;
  };

  const [formData, setFormData] = useState<SignupFormData>({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    role: 'client',
    nationalId: '',
    iban: '',
    jobTitle: '',
    monthlyIncome: 0,
    monthlyCommitments: 0,
    licenseNumber: '',
    commercialRegistryNumber: '',
    officeLocation: '',
    licensePhoto: null,
    registryPhoto: null,
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleRoleSelect = (selectedRole: UserRole) => {
    setRole(selectedRole);
    setStep(2);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    } as SignupFormData));
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    
    setFormData(prev => ({
      ...prev,
      [name]: numValue
    } as SignupFormData));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.email || !formData.password || !formData.fullName || !formData.phone) {
      setError('الرجاء تعبئة جميع الحقول المطلوبة');
      return;
    }

    // Password confirmation
    if (formData.password !== confirmPassword) {
      setError('كلمة المرور غير متطابقة');
      return;
    }

    // Role-specific validation
    if (role === 'client') {
      if (!formData.nationalId || !formData.iban || !formData.jobTitle) {
        setError('الرجاء تعبئة جميع الحقول المطلوبة');
        return;
      }
    } else {
      if (!formData.licenseNumber || !formData.commercialRegistryNumber || !formData.officeLocation) {
        setError('الرجاء تعبئة جميع الحقول المطلوبة');
        return;
      }
    }

    try {
      setIsLoading(true);
      setLoading(true);
      
      // Prepare user data for signup
      const userData = {
        ...formData,
        role,
        // Ensure numbers are properly typed as numbers
        monthlyIncome: typeof formData.monthlyIncome === 'string' 
          ? parseFloat(formData.monthlyIncome) || 0 
          : formData.monthlyIncome || 0,
        monthlyCommitments: typeof formData.monthlyCommitments === 'string'
          ? parseFloat(formData.monthlyCommitments) || 0
          : formData.monthlyCommitments || 0,
      };

      // Convert to UserData type expected by signUp
      const userDataForSignUp = {
        ...userData,
        monthlyIncome: Number(userData.monthlyIncome),
        monthlyCommitments: Number(userData.monthlyCommitments),
      };

      await signUp(userData.email, userData.password, userDataForSignUp);
      navigate('/dashboard');
    } catch (err) {
      console.error('Signup error:', err);
      setError('فشل إنشاء الحساب. الرجاء المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  // Step 1: Account Type Selection
  const renderAccountTypeStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">إنشاء حساب جديد</h2>
        <p className="mt-2 text-sm text-gray-600">
          اختر نوع الحساب الذي تريد إنشاءه
        </p>
      </div>

      <div className="space-y-4">
        <button
          type="button"
          onClick={() => handleRoleSelect('client')}
          style={{
            width: '100%',
            padding: '1.5rem',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            textAlign: 'right',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            outline: 'none'
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = '#ffffff'}
        >
          <div style={{
            height: '3rem',
            width: '3rem',
            borderRadius: '9999px',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <User style={{ height: '1.5rem', width: '1.5rem', color: '#3b82f6' }} />
          </div>
          <div style={{ textAlign: 'right', flex: 1, marginRight: '1rem' }}>
            <h3 style={{ fontWeight: 500, color: '#111827' }}>عميل</h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>أريد الاستفادة من الخدمات القانونية</p>
          </div>
          <ChevronLeft style={{ height: '1.25rem', width: '1.25rem', color: '#9ca3af' }} />
        </button>

        <button
          type="button"
          onClick={() => handleRoleSelect('lawyer')}
          style={{
            width: '100%',
            padding: '1.5rem',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            textAlign: 'right',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            outline: 'none'
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = '#ffffff'}
        >
          <div style={{
            height: '3rem',
            width: '3rem',
            borderRadius: '9999px',
            backgroundColor: '#ecfdf5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Scale style={{ height: '1.5rem', width: '1.5rem', color: '#10b981' }} />
          </div>
          <div style={{ textAlign: 'right', flex: 1, marginRight: '1rem' }}>
            <h3 style={{ fontWeight: 500, color: '#111827' }}>محامي</h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>أريد تقديم الخدمات القانونية</p>
          </div>
          <ChevronLeft style={{ height: '1.25rem', width: '1.25rem', color: '#9ca3af' }} />
        </button>
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          لديك حساب بالفعل؟{' '}
          <Link 
            to="/login" 
            style={{
              color: '#3b82f6',
              fontWeight: 500,
              textDecoration: 'none'
            }}
            onMouseOver={e => e.currentTarget.style.color = '#2563eb'}
            onMouseOut={e => e.currentTarget.style.color = '#3b82f6'}
          >
            تسجيل الدخول
          </Link>
        </p>
      </div>
    </div>
  );

  // Step 2: Registration Form
  const renderRegistrationForm = () => {
    const isClient = role === 'client';
    
    return (
      <form id="signup-form" onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {isClient ? 'تسجيل عميل جديد' : 'تسجيل محامي جديد'}
          </h2>
        </div>

        <div className="space-y-4">
          <Input
            label="الاسم الكامل"
            id="fullName"
            name="fullName"
            type="text"
            required
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="أدخل الاسم الكامل"
          />

          <Input
            label="رقم الجوال"
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="05XXXXXXXX"
          />
          <Input
            label="البريد الإلكتروني"
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            placeholder="example@example.com"
          />
          <Input
            label="كلمة المرور"
            id="password"
            name="password"
            type="password"
            required
            value={formData.password}
            onChange={handleInputChange}
            placeholder="••••••••"
          />
          <Input
            label="تأكيد كلمة المرور"
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />
          {isClient ? (
            <>
              <Input
                label="الرقم الوطني"
                id="nationalId"
                name="nationalId"
                type="text"
                required
                value={formData.nationalId || ''}
                onChange={handleInputChange}
                placeholder="أدخل الرقم الوطني"
              />
              <Input
                label="رقم الآيبان"
                id="iban"
                name="iban"
                type="text"
                required
                value={formData.iban || ''}
                onChange={handleInputChange}
                placeholder="SAXXXXXXXXXXXXXXXXXXXXXXXX"
              />
              <Input
                label="المسمى الوظيفي"
                id="jobTitle"
                name="jobTitle"
                type="text"
                required
                value={formData.jobTitle || ''}
                onChange={handleInputChange}
                placeholder="أدخل المسمى الوظيفي"
              />
              <Input
                label="الدخل الشهري"
                id="monthlyIncome"
                name="monthlyIncome"
                type="number"
                required
                value={formData.monthlyIncome.toString()}
                onChange={handleNumberInputChange}
                placeholder="أدخل الدخل الشهري"
              />
              <Input
                label="الالتزامات الشهرية"
                id="monthlyCommitments"
                name="monthlyCommitments"
                type="number"
                value={formData.monthlyCommitments.toString()}
                onChange={handleNumberInputChange}
                placeholder="أدخل الالتزامات الشهرية (اختياري)"
              />
            </>
          ) : (
            <>
              <Input
                label="رقم الرخصة المهنية"
                id="licenseNumber"
                name="licenseNumber"
                type="text"
                required
                value={formData.licenseNumber || ''}
                onChange={handleInputChange}
                placeholder="أدخل رقم الرخصة المهنية"
              />
              <Input
                label="رقم السجل التجاري"
                id="commercialRegistryNumber"
                name="commercialRegistryNumber"
                type="text"
                required
                value={formData.commercialRegistryNumber || ''}
                onChange={handleInputChange}
                placeholder="أدخل رقم السجل التجاري"
              />
              <Input
                label="موقع المكتب"
                id="officeLocation"
                name="officeLocation"
                type="text"
                required
                value={formData.officeLocation || ''}
                onChange={handleInputChange}
                placeholder="أدخل موقع المكتب"
              />
              {/* File upload sections removed as requested */}
            </>
          )}
        </div>
        
        {error && (
          <div className="p-4 mt-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}
      </form>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-DancingScript font-bold text-blue-600 mb-8">LexPay</h1>
      </div>

      {step === 1 ? (
        renderAccountTypeStep()
      ) : (
        <div className="max-w-md bg-white p-8 rounded-2xl shadow-sm">


          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 text-sm rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 ml-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {renderRegistrationForm()}

          <div className="mt-6">
            <button
              type="submit"
              form="signup-form"
              disabled={isLoading}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '0.75rem 1rem',
                border: '1px solid transparent',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#ffffff',
                backgroundColor: 'oklch(0.55 0.24 262.91)',
                transition: 'background-color 0.2s, opacity 0.2s',
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                outline: 'none'
              }}
              onMouseOver={e => !isLoading && (e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)')}
              onMouseOut={e => !isLoading && (e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)')}
              onFocus={e => !isLoading && (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)')}
              onBlur={e => !isLoading && (e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)')}
            >
              {isLoading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
            </button>
          </div>

          <div style={{
            marginTop: '1rem',
            textAlign: 'center',
            fontSize: '0.875rem'
          }}>
            <p style={{
              color: '#6b7280'
            }}>
              لديك حساب بالفعل؟{' '}
               <Link 
                 to="/login" 
                 style={{
                   fontWeight: 500,
                   color: '#2563eb',
                   textDecoration: 'none'
                 }}
                 onMouseOver={e => e.currentTarget.style.color = '#3b82f6'}
                 onMouseOut={e => e.currentTarget.style.color = '#2563eb'}
               >
                 تسجيل الدخول
               </Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupPage;
